import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type {
  Session as SessionType,
  SessionHighlights,
  IntroductionHighlights,
  WarmupHighlights,
  VocabularyHighlights,
  GrammarHighlights,
  PracticeHighlights,
  CheckpointHighlights,
} from '@/types/database'
import { analytics } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  ArrowLeft,
  Plus,
  Sparkles,
  Target,
  ArrowRight,
  AlertCircle,
  Star,
  BookOpen,
  PenTool,
  Lightbulb,
  Check,
  Mic,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Heart,
  MessageCircle,
  Zap,
  Trophy,
  PartyPopper,
  GraduationCap,
  Volume2,
} from 'lucide-react'

// Small tutor avatar for session summary
function SmallTutorAvatar() {
  return (
    <div className="relative flex-shrink-0">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-lg scale-125" />
      <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-white shadow-md flex items-center justify-center overflow-hidden">
        <svg viewBox="0 0 100 100" className="w-11 h-11">
          <circle cx="50" cy="50" r="45" fill="#FEF3C7" />
          <circle cx="25" cy="55" r="8" fill="#FECACA" opacity="0.6" />
          <circle cx="75" cy="55" r="8" fill="#FECACA" opacity="0.6" />
          <ellipse cx="35" cy="42" rx="6" ry="7" fill="#1F2937" />
          <ellipse cx="65" cy="42" rx="6" ry="7" fill="#1F2937" />
          <circle cx="37" cy="40" r="2" fill="white" />
          <circle cx="67" cy="40" r="2" fill="white" />
          <path d="M 35 62 Q 50 78 65 62" fill="none" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" />
          <path d="M 28 32 Q 35 28 42 32" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
          <path d="M 58 32 Q 65 28 72 32" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  )
}

// Tutor message speech bubble
function TutorMessage({ score, summary }: { score: number; summary: string }) {
  const getMessage = () => {
    if (score >= 90) return "Amazing work! You're really mastering this!"
    if (score >= 80) return "Great session! You're making excellent progress!"
    if (score >= 70) return "Nice job! Keep up the good practice!"
    if (score >= 60) return "Good effort! Every session helps you improve!"
    return "Thanks for practicing! Consistency is key to learning!"
  }

  return (
    <div className="relative animate-fade-in" style={{ animationDelay: '0.1s' }}>
      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md border border-amber-100">
        <p className="text-gray-800 font-medium">{getMessage()}</p>
        <p className="text-gray-600 text-sm mt-1">{summary}</p>
      </div>
      <div className="absolute -top-0 -left-2 w-3 h-3 bg-white border-l border-t border-amber-100 transform rotate-[-45deg] rounded-tl-sm" />
    </div>
  )
}

// Score display with color coding
function ScoreDisplay({ score }: { score: number }) {
  const getColorClasses = () => {
    if (score >= 80) return {
      bg: 'from-emerald-400 to-teal-500',
      glow: 'bg-emerald-400/30',
      text: 'text-white',
    }
    if (score >= 60) return {
      bg: 'from-amber-400 to-orange-500',
      glow: 'bg-amber-400/30',
      text: 'text-white',
    }
    return {
      bg: 'from-orange-400 to-rose-400',
      glow: 'bg-orange-400/30',
      text: 'text-white',
    }
  }

  const colors = getColorClasses()

  return (
    <div className="relative">
      {/* Glow effect */}
      <div className={`absolute inset-0 ${colors.glow} rounded-full blur-xl scale-125 animate-pulse-soft`} />

      {/* Score circle */}
      <div className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${colors.bg} shadow-lg flex items-center justify-center`}>
        <div className="text-center">
          <div className={`text-3xl font-bold ${colors.text}`}>{score}</div>
          <div className={`text-xs ${colors.text} opacity-80`}>Score</div>
        </div>
      </div>

      {/* Sparkles for high scores */}
      {score >= 80 && (
        <>
          <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-400 animate-sparkle-twinkle" />
          <Sparkles className="absolute -bottom-1 -left-1 w-4 h-4 text-amber-400 animate-sparkle-twinkle" style={{ animationDelay: '0.5s' }} />
        </>
      )}
    </div>
  )
}

// Warm card wrapper
function WarmCard({
  children,
  className = '',
  animationDelay = '0s',
  gradient = 'amber',
}: {
  children: React.ReactNode
  className?: string
  animationDelay?: string
  gradient?: 'amber' | 'emerald' | 'purple' | 'blue'
}) {
  const gradientClasses = {
    amber: {
      card: 'from-amber-50/80 via-white to-orange-50/80 border-amber-200/50',
      bar: 'from-amber-400 via-orange-400 to-rose-400',
    },
    emerald: {
      card: 'from-emerald-50/80 via-white to-teal-50/80 border-emerald-200/50',
      bar: 'from-emerald-400 via-teal-400 to-emerald-500',
    },
    purple: {
      card: 'from-purple-50/80 via-white to-pink-50/80 border-purple-200/50',
      bar: 'from-purple-400 via-pink-400 to-purple-500',
    },
    blue: {
      card: 'from-blue-50/80 via-white to-indigo-50/80 border-blue-200/50',
      bar: 'from-blue-400 via-indigo-400 to-blue-500',
    },
  }

  const colors = gradientClasses[gradient]

  return (
    <div
      className={`bg-gradient-to-br ${colors.card} rounded-2xl border shadow-lg overflow-hidden animate-fade-in ${className}`}
      style={{ animationDelay }}
    >
      {/* Decorative top bar */}
      <div className={`h-1.5 bg-gradient-to-r ${colors.bar}`} />
      {children}
    </div>
  )
}

// Simple message bubble without score
function SimpleTutorMessage({ message, summary }: { message: string; summary: string }) {
  return (
    <div className="relative animate-fade-in" style={{ animationDelay: '0.1s' }}>
      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md border border-amber-100">
        <p className="text-gray-800 font-medium">{message}</p>
        <p className="text-gray-600 text-sm mt-1">{summary}</p>
      </div>
      <div className="absolute -top-0 -left-2 w-3 h-3 bg-white border-l border-t border-amber-100 transform rotate-[-45deg] rounded-tl-sm" />
    </div>
  )
}

// ============================================
// TYPE-SPECIFIC HIGHLIGHT RENDERERS
// ============================================

// Introduction Highlights UI
function IntroductionHighlightsUI({ highlights }: { highlights: IntroductionHighlights }) {
  return (
    <div className="space-y-6">
      {/* Welcome Hero */}
      <WarmCard className="relative" gradient="purple">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-400/30 rounded-full blur-xl scale-125 animate-pulse-soft" />
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 shadow-lg flex items-center justify-center">
                  <Heart className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
            <div className="flex-1 flex items-start gap-4">
              <SmallTutorAvatar />
              <SimpleTutorMessage message="Welcome! Great to meet you!" summary={highlights.summary} />
            </div>
          </div>
        </div>
      </WarmCard>

      {/* Level Assessment */}
      <WarmCard animationDelay="0.1s">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-800">Your Starting Point</h2>
          </div>
          <div className="bg-white/60 rounded-xl p-4 border border-amber-100/50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-2xl font-bold text-gray-800">{highlights.level_assessment.assessed_level}</p>
              <span className="px-3 py-1 text-sm rounded-full bg-amber-100 text-amber-800">
                {highlights.level_assessment.confidence}% confidence
              </span>
            </div>
            <p className="text-gray-600 text-sm">{highlights.level_assessment.reasoning}</p>
          </div>
        </div>
      </WarmCard>

      {/* Goals & Interests Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Goals */}
        {highlights.goals_discovered.length > 0 && (
          <WarmCard animationDelay="0.15s">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-gray-800">Your Goals</h3>
              </div>
              <ul className="space-y-2">
                {highlights.goals_discovered.map((goal, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <span className="text-amber-500 mt-0.5">•</span>
                    {goal}
                  </li>
                ))}
              </ul>
            </div>
          </WarmCard>
        )}

        {/* Interests */}
        {highlights.interests_discovered.length > 0 && (
          <WarmCard animationDelay="0.2s">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-gray-800">Topics You Like</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {highlights.interests_discovered.map((interest, i) => (
                  <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          </WarmCard>
        )}
      </div>

      {/* Initial Abilities */}
      {highlights.initial_abilities.length > 0 && (
        <WarmCard animationDelay="0.25s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">What You Already Know</h3>
            </div>
            <ul className="space-y-3">
              {highlights.initial_abilities.map((ability, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    ability.level_indicator === 'advanced' ? 'bg-emerald-100 text-emerald-700' :
                    ability.level_indicator === 'intermediate' ? 'bg-blue-100 text-blue-700' :
                    ability.level_indicator === 'basic' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {ability.level_indicator}
                  </span>
                  <span className="text-gray-700">{ability.observation}</span>
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}

      {/* Personality Notes */}
      {highlights.personality_notes.length > 0 && (
        <WarmCard animationDelay="0.3s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Learning Style Notes</h3>
            </div>
            <ul className="space-y-2">
              {highlights.personality_notes.map((note, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Warmup Highlights UI
function WarmupHighlightsUI({ highlights }: { highlights: WarmupHighlights }) {
  return (
    <div className="space-y-6">
      {/* Ready Hero */}
      <WarmCard className="relative" gradient="blue">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-400/30 rounded-full blur-xl scale-125 animate-pulse-soft" />
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg flex items-center justify-center">
                  <Zap className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
            <div className="flex-1 flex items-start gap-4">
              <SmallTutorAvatar />
              <SimpleTutorMessage message="Warmed up and ready!" summary={highlights.summary} />
            </div>
          </div>
        </div>
      </WarmCard>

      {/* Topics & Engagement */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Topics Discussed */}
        {highlights.topics_discussed.length > 0 && (
          <WarmCard animationDelay="0.1s" gradient="blue">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-800">Topics Discussed</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {highlights.topics_discussed.map((topic, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          </WarmCard>
        )}

        {/* Engagement Level */}
        <WarmCard animationDelay="0.15s" gradient="blue">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Engagement</h3>
            </div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
              highlights.engagement_level === 'high' ? 'bg-emerald-100 text-emerald-700' :
              highlights.engagement_level === 'medium' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {highlights.engagement_level === 'high' && <Star className="w-4 h-4" />}
              <span className="font-medium capitalize">{highlights.engagement_level}</span>
            </div>
            <p className="text-gray-600 text-sm mt-3">{highlights.readiness_for_lesson}</p>
          </div>
        </WarmCard>
      </div>

      {/* Vocabulary Preview */}
      {highlights.vocabulary_previewed.length > 0 && (
        <WarmCard animationDelay="0.2s" gradient="blue">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Words Previewed</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {highlights.vocabulary_previewed.map((word, i) => (
                <span key={i} className="px-3 py-1.5 bg-white/60 border border-blue-200 text-gray-800 rounded-lg text-sm">
                  {word}
                </span>
              ))}
            </div>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Vocabulary Highlights UI
function VocabularyHighlightsUI({ highlights }: { highlights: VocabularyHighlights }) {
  return (
    <div className="space-y-6">
      {/* Score Card */}
      <WarmCard className="relative">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <ScoreDisplay score={highlights.overall_score} />
            </div>
            <div className="flex-1 flex items-start gap-4">
              <SmallTutorAvatar />
              <TutorMessage score={highlights.overall_score} summary={highlights.summary} />
            </div>
          </div>
        </div>
      </WarmCard>

      {/* Words Learned */}
      <WarmCard animationDelay="0.1s">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-800">Words Learned</h3>
          </div>
          <div className="space-y-3">
            {highlights.words_learned.map((word, i) => (
              <div
                key={i}
                className="bg-white/60 rounded-xl p-4 border border-amber-100/50 animate-fade-in"
                style={{ animationDelay: `${0.15 + i * 0.05}s` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-gray-800">{word.word}</p>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 text-sm rounded-full bg-amber-100 text-amber-800">
                      {word.translation}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      word.mastery === 'learned' ? 'bg-emerald-100 text-emerald-700' :
                      word.mastery === 'practiced' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {word.mastery === 'learned' ? '✓ Learned' :
                       word.mastery === 'practiced' ? '◐ Practiced' :
                       '○ Review'}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 italic">"{word.context}"</p>
              </div>
            ))}
          </div>
        </div>
      </WarmCard>

      {/* Pronunciation Notes */}
      {highlights.pronunciation_notes.length > 0 && (
        <WarmCard animationDelay="0.2s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Volume2 className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Pronunciation Notes</h3>
            </div>
            <ul className="space-y-2">
              {highlights.pronunciation_notes.map((note, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}

      {/* Recommendations */}
      {highlights.recommendations.length > 0 && (
        <WarmCard animationDelay="0.25s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Recommendations</h3>
            </div>
            <ul className="space-y-2">
              {highlights.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Grammar Highlights UI
function GrammarHighlightsUI({ highlights }: { highlights: GrammarHighlights }) {
  return (
    <div className="space-y-6">
      {/* Score Card */}
      <WarmCard className="relative">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <ScoreDisplay score={highlights.overall_score} />
            </div>
            <div className="flex-1 flex items-start gap-4">
              <SmallTutorAvatar />
              <TutorMessage score={highlights.overall_score} summary={highlights.summary} />
            </div>
          </div>
        </div>
      </WarmCard>

      {/* Grammar Covered */}
      <WarmCard animationDelay="0.1s">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <PenTool className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-800">Grammar Covered</h3>
          </div>
          <div className="space-y-4">
            {highlights.grammar_covered.map((grammar, i) => (
              <div
                key={i}
                className="bg-white/60 rounded-xl p-4 border border-amber-100/50 animate-fade-in"
                style={{ animationDelay: `${0.15 + i * 0.05}s` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-gray-800">{grammar.rule}</p>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    grammar.understanding === 'solid' ? 'bg-emerald-100 text-emerald-700' :
                    grammar.understanding === 'partial' ? 'bg-amber-100 text-amber-700' :
                    'bg-rose-100 text-rose-700'
                  }`}>
                    {grammar.understanding === 'solid' ? '✓ Solid' :
                     grammar.understanding === 'partial' ? '◐ Partial' :
                     '○ Review needed'}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {grammar.examples.map((example, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-amber-500 mt-0.5">•</span>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </WarmCard>

      {/* Mistakes */}
      {highlights.mistakes.length > 0 && (
        <WarmCard animationDelay="0.2s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-rose-500" />
              <h3 className="font-semibold text-gray-800">Areas to Improve</h3>
            </div>
            <div className="space-y-3">
              {highlights.mistakes.map((mistake, i) => (
                <div key={i} className="bg-rose-50/50 rounded-xl p-4 border border-rose-200/50">
                  <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-700 mb-2">
                    {mistake.type}
                  </span>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">You said:</p>
                      {mistake.error_phrase && mistake.correction_phrase && mistake.original.includes(mistake.error_phrase) ? (
                        <p className="text-gray-800">
                          {(() => {
                            const idx = mistake.original.indexOf(mistake.error_phrase)
                            const before = mistake.original.slice(0, idx)
                            const after = mistake.original.slice(idx + mistake.error_phrase.length)
                            return (
                              <>
                                {before}
                                <span className="line-through text-rose-600 bg-rose-50 px-1 rounded">{mistake.error_phrase}</span>
                                <span className="text-emerald-600 bg-emerald-50 px-1 rounded ml-1">→ {mistake.correction_phrase}</span>
                                {after}
                              </>
                            )
                          })()}
                        </p>
                      ) : (
                        <>
                          <p className="line-through text-rose-600">{mistake.original}</p>
                          <p className="text-emerald-600 font-medium">{mistake.correction}</p>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 pl-3 border-l-2 border-amber-300">{mistake.explanation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </WarmCard>
      )}

      {/* Recommendations */}
      {highlights.recommendations.length > 0 && (
        <WarmCard animationDelay="0.25s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Recommendations</h3>
            </div>
            <ul className="space-y-2">
              {highlights.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Checkpoint Highlights UI
function CheckpointHighlightsUI({ highlights }: { highlights: CheckpointHighlights }) {
  return (
    <div className="space-y-6">
      {/* Celebration Hero */}
      <WarmCard className="relative" gradient="purple">
        <div className="absolute top-5 left-5 w-24 h-24 bg-gradient-to-br from-yellow-200/40 to-amber-200/40 rounded-full blur-2xl" />
        <div className="absolute bottom-5 right-5 w-32 h-32 bg-gradient-to-br from-purple-200/40 to-pink-200/40 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative text-center">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg mb-4">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <div className="flex justify-center gap-2 mb-2">
              <PartyPopper className="w-6 h-6 text-purple-500" />
              <h2 className="text-2xl font-bold text-gray-800">Chapter Complete!</h2>
              <PartyPopper className="w-6 h-6 text-purple-500 scale-x-[-1]" />
            </div>
          </div>
          <p className="text-gray-700 text-lg max-w-xl mx-auto">{highlights.celebration_message}</p>
        </div>
      </WarmCard>

      {/* Chapter Reflection */}
      <WarmCard animationDelay="0.1s" gradient="purple">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-800">What You Accomplished</h2>
          </div>
          <p className="text-gray-700">{highlights.chapter_reflection}</p>
          <p className="text-gray-600 text-sm mt-3">{highlights.summary}</p>
        </div>
      </WarmCard>

      {/* Skills & Growth Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Skills Demonstrated */}
        {highlights.skills_demonstrated.length > 0 && (
          <WarmCard animationDelay="0.15s" gradient="emerald">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-gray-800">Skills You Can Now Do</h3>
              </div>
              <ul className="space-y-2">
                {highlights.skills_demonstrated.map((skill, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          </WarmCard>
        )}

        {/* Growth Areas */}
        {highlights.growth_areas.length > 0 && (
          <WarmCard animationDelay="0.2s">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-gray-800">Areas of Growth</h3>
              </div>
              <ul className="space-y-2">
                {highlights.growth_areas.map((area, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </WarmCard>
        )}
      </div>

      {/* Goals for Next Chapter */}
      {highlights.goals_for_next_chapter.length > 0 && (
        <WarmCard animationDelay="0.25s" gradient="blue">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-800">What's Next</h2>
            </div>
            <ul className="space-y-3">
              {highlights.goals_for_next_chapter.map((goal, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                    <ArrowRight className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">{goal}</span>
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Practice Highlights UI (for roleplay, conversation sessions)
function PracticeHighlightsUI({ highlights }: { highlights: PracticeHighlights }) {
  return (
    <div className="space-y-6">
      {/* Main Score Card */}
      <WarmCard className="relative">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-2xl" />
        <div className="p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <ScoreDisplay score={highlights.overall_score} />
            </div>
            <div className="flex-1 flex items-start gap-4">
              <SmallTutorAvatar />
              <TutorMessage score={highlights.overall_score} summary={highlights.summary} />
            </div>
          </div>
        </div>
      </WarmCard>

      {/* Scenario Performance */}
      {highlights.scenario_performance && (
        <WarmCard animationDelay="0.1s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Scenario Performance</h3>
            </div>
            <p className="text-gray-700">{highlights.scenario_performance}</p>
          </div>
        </WarmCard>
      )}

      {/* Tabs Section */}
      <div className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
        <Tabs defaultValue="mistakes">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-1 h-auto p-1.5 bg-amber-100/50 border border-amber-200/50 rounded-xl">
            <TabsTrigger value="mistakes" className="flex items-center gap-1.5 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-amber-700 rounded-lg transition-all">
              <AlertCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Mistakes</span>
              {highlights.mistakes.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-rose-100 text-rose-700">{highlights.mistakes.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="strengths" className="flex items-center gap-1.5 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-amber-700 rounded-lg transition-all">
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline">Strengths</span>
            </TabsTrigger>
            <TabsTrigger value="vocabulary" className="flex items-center gap-1.5 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-amber-700 rounded-lg transition-all">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Vocab</span>
            </TabsTrigger>
            <TabsTrigger value="fluency" className="flex items-center gap-1.5 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-amber-700 rounded-lg transition-all">
              <Volume2 className="w-4 h-4" />
              <span className="hidden sm:inline">Fluency</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mistakes" className="space-y-4 mt-4">
            {highlights.mistakes.length === 0 ? (
              <WarmCard>
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-gray-700 font-medium">No mistakes recorded. Great job!</p>
                </div>
              </WarmCard>
            ) : (
              highlights.mistakes.map((mistake, i) => (
                <div key={i} className="bg-gradient-to-br from-rose-50/80 via-white to-amber-50/50 rounded-2xl border border-rose-200/50 shadow-md overflow-hidden animate-fade-in" style={{ animationDelay: `${0.2 + i * 0.05}s` }}>
                  <div className="h-1 bg-gradient-to-r from-rose-300 via-rose-400 to-amber-300" />
                  <div className="p-4 md:p-5">
                    <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-rose-100 text-rose-700 mb-3">{mistake.type}</span>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">You said:</p>
                        {mistake.error_phrase && mistake.correction_phrase && mistake.original.includes(mistake.error_phrase) ? (
                          <p className="text-gray-800">
                            {(() => {
                              const idx = mistake.original.indexOf(mistake.error_phrase)
                              const before = mistake.original.slice(0, idx)
                              const after = mistake.original.slice(idx + mistake.error_phrase.length)
                              return (
                                <>
                                  {before}
                                  <span className="line-through text-rose-600 bg-rose-50 px-1 rounded">{mistake.error_phrase}</span>
                                  <span className="text-emerald-600 bg-emerald-50 px-1 rounded ml-1">→ {mistake.correction_phrase}</span>
                                  {after}
                                </>
                              )
                            })()}
                          </p>
                        ) : (
                          <>
                            <p className="line-through text-rose-600">{mistake.original}</p>
                            <p className="text-emerald-600 font-medium">{mistake.correction}</p>
                          </>
                        )}
                      </div>
                      <div className="pt-2 pl-3 border-l-2 border-amber-300">
                        <p className="text-sm text-gray-600">{mistake.explanation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="strengths" className="mt-4">
            <WarmCard>
              <div className="p-5">
                {highlights.strengths.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Keep practicing to build your strengths!</p>
                ) : (
                  <ul className="space-y-3">
                    {highlights.strengths.map((strength, i) => (
                      <li key={i} className="flex items-start gap-3 animate-fade-in" style={{ animationDelay: `${0.2 + i * 0.05}s` }}>
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        </div>
                        <span className="text-gray-700">{strength}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </WarmCard>
          </TabsContent>

          <TabsContent value="vocabulary" className="space-y-4 mt-4">
            {highlights.vocabulary_used.length === 0 ? (
              <WarmCard>
                <div className="p-8 text-center text-gray-500">No vocabulary tracked this session</div>
              </WarmCard>
            ) : (
              highlights.vocabulary_used.map((vocab, i) => (
                <WarmCard key={i} animationDelay={`${0.2 + i * 0.05}s`}>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-lg text-gray-800">{vocab.word}</p>
                      <span className="px-2.5 py-1 text-sm rounded-full bg-amber-100 text-amber-800">{vocab.translation}</span>
                    </div>
                    <p className="text-sm text-gray-600 italic">"{vocab.context}"</p>
                  </div>
                </WarmCard>
              ))
            )}
          </TabsContent>

          <TabsContent value="fluency" className="mt-4">
            <WarmCard>
              <div className="p-5">
                {highlights.fluency_notes.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No fluency notes this session</p>
                ) : (
                  <ul className="space-y-3">
                    {highlights.fluency_notes.map((note, i) => (
                      <li key={i} className="flex items-start gap-3 animate-fade-in" style={{ animationDelay: `${0.2 + i * 0.05}s` }}>
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span className="text-gray-700">{note}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </WarmCard>
          </TabsContent>
        </Tabs>
      </div>

      {/* Level Assessment */}
      {highlights.level_assessment && (
        <WarmCard animationDelay="0.25s">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-800">Level Assessment</h2>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
              <div className="flex-1 w-full bg-white/60 rounded-xl p-4 text-center border border-amber-100/50">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your level</p>
                <p className="text-xl font-bold text-gray-800">{highlights.level_assessment.current_level}</p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-amber-600 rotate-90 md:rotate-0" />
                </div>
              </div>
              <div className="flex-1 w-full bg-white/60 rounded-xl p-4 text-center border border-amber-100/50">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Observed</p>
                <p className="text-xl font-bold text-gray-800">{highlights.level_assessment.observed_level}</p>
              </div>
            </div>
            {highlights.level_assessment.suggestion && (
              <div className={`rounded-xl p-4 ${
                highlights.level_assessment.suggestion === 'advance' ? 'bg-emerald-50 border border-emerald-200' :
                highlights.level_assessment.suggestion === 'review' ? 'bg-amber-50 border border-amber-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {highlights.level_assessment.suggestion === 'advance' && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                  {highlights.level_assessment.suggestion === 'review' && <TrendingDown className="w-4 h-4 text-amber-600" />}
                  {highlights.level_assessment.suggestion === 'stay' && <Minus className="w-4 h-4 text-blue-600" />}
                  <p className={`font-medium ${
                    highlights.level_assessment.suggestion === 'advance' ? 'text-emerald-800' :
                    highlights.level_assessment.suggestion === 'review' ? 'text-amber-800' : 'text-blue-800'
                  }`}>
                    {highlights.level_assessment.suggestion === 'advance' && 'Consider advancing to the next level!'}
                    {highlights.level_assessment.suggestion === 'review' && 'You might benefit from reviewing basics'}
                    {highlights.level_assessment.suggestion === 'stay' && 'Your current level is appropriate'}
                  </p>
                </div>
                <p className={`text-sm ${
                  highlights.level_assessment.suggestion === 'advance' ? 'text-emerald-700' :
                  highlights.level_assessment.suggestion === 'review' ? 'text-amber-700' : 'text-blue-700'
                }`}>
                  {highlights.level_assessment.reasoning}
                </p>
              </div>
            )}
          </div>
        </WarmCard>
      )}

      {/* Recommendations */}
      {highlights.recommendations.length > 0 && (
        <WarmCard animationDelay="0.3s">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Recommendations</h3>
            </div>
            <ul className="space-y-2">
              {highlights.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </WarmCard>
      )}
    </div>
  )
}

// Main router for highlights rendering
function HighlightsRenderer({ highlights }: { highlights: SessionHighlights }) {
  switch (highlights.type) {
    case 'introduction':
      return <IntroductionHighlightsUI highlights={highlights} />
    case 'warmup':
      return <WarmupHighlightsUI highlights={highlights} />
    case 'vocabulary':
      return <VocabularyHighlightsUI highlights={highlights} />
    case 'grammar':
      return <GrammarHighlightsUI highlights={highlights} />
    case 'practice':
      return <PracticeHighlightsUI highlights={highlights} />
    case 'checkpoint':
      return <CheckpointHighlightsUI highlights={highlights} />
  }
}

export function Session() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { profile } = useAuth()
  const [session, setSession] = useState<SessionType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [waitingForRoadmap, setWaitingForRoadmap] = useState(false)
  const summaryViewedRef = useRef(false)

  useEffect(() => {
    const fetchSession = async () => {
      if (!id) return

      const result = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id)
        .single()

      if (result.error) {
        setError('Session not found')
      } else {
        setSession(result.data as SessionType)
      }
      setIsLoading(false)
    }

    fetchSession()
  }, [id])

  // Track when session summary is first viewed
  useEffect(() => {
    const terminalStates = ['completed', 'failed', 'cancelled']
    if (session && id && terminalStates.includes(session.status) && !summaryViewedRef.current) {
      summaryViewedRef.current = true
      const highlights = session.highlights as SessionHighlights | null
      // Extract score - only some highlight types have overall_score
      let score: number | null = null
      if (highlights && 'overall_score' in highlights) {
        score = highlights.overall_score
      }
      analytics.sessionSummaryViewed(id, score, session.status)
    }
  }, [session, id])

  // Handle introduction session completion - wait for roadmap generation
  useEffect(() => {
    const isIntroduction = session?.lesson_type === 'introduction'
    const isCompleted = session?.status === 'completed'

    if (isIntroduction && isCompleted && !waitingForRoadmap) {
      setWaitingForRoadmap(true)
    }
  }, [session, waitingForRoadmap])

  // Poll for onboarding completion after introduction session
  useEffect(() => {
    if (!waitingForRoadmap || !profile?.id) return

    // If already onboarded, redirect immediately
    if (profile.onboarding_completed) {
      navigate('/roadmap-ready')
      return
    }

    // Poll for onboarding completion
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('users')
        .select('onboarding_completed')
        .eq('id', profile.id)
        .single()

      const result = data as { onboarding_completed: boolean } | null
      if (result?.onboarding_completed) {
        clearInterval(interval)
        navigate('/roadmap-ready')
      }
    }, 2000)

    // Timeout after 60 seconds and show fallback
    const timeout = setTimeout(() => {
      clearInterval(interval)
      // If still not ready, navigate home (they can check later)
      navigate('/')
    }, 60000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [waitingForRoadmap, profile?.id, profile?.onboarding_completed, navigate])

  useEffect(() => {
    // Stop polling once session reaches a terminal state
    const terminalStates = ['completed', 'failed', 'cancelled']
    if (!session || !id || terminalStates.includes(session.status)) return

    const interval = setInterval(async () => {
      const result = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id)
        .single()

      const data = result.data as SessionType | null
      if (data && terminalStates.includes(data.status)) {
        setSession(data)
        clearInterval(interval)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [session, id])

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        {/* Decorative background */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-br from-orange-200/20 to-rose-200/20 rounded-full blur-3xl" />

        <WarmCard className="w-full max-w-md relative">
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-rose-500" />
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25"
            >
              Go Home
            </Button>
          </div>
        </WarmCard>
      </div>
    )
  }

  // Show roadmap generation loading state for introduction sessions
  if (waitingForRoadmap) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        {/* Decorative background */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-br from-orange-200/20 to-rose-200/20 rounded-full blur-3xl" />

        <WarmCard className="w-full max-w-md relative">
          <div className="p-8 text-center space-y-6">
            <div className="flex justify-center">
              <SmallTutorAvatar />
            </div>
            <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-amber-500 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold text-lg text-gray-800">Creating your personalized plan...</p>
              <p className="text-sm text-gray-600 mt-2">
                Based on our conversation, I'm building a learning path just for you.
              </p>
            </div>
            <div className="w-12 h-12 mx-auto border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        </WarmCard>
      </div>
    )
  }

  // Initial loading state (fetching from API)
  if (isLoading || !session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    )
  }

  // Waiting for highlights generation (status is processing/active)
  if (session.status === 'active' || session.status === 'processing') {
    const isIntroduction = session.lesson_type === 'introduction'

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        {/* Decorative background */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-br from-orange-200/20 to-rose-200/20 rounded-full blur-3xl" />

        <WarmCard className="w-full max-w-md relative">
          <div className="p-8 text-center space-y-6">
            <div className="flex justify-center animate-bounce-in">
              <SmallTutorAvatar />
            </div>
            <div className="w-12 h-12 mx-auto border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <p className="font-semibold text-lg text-gray-800">
                {isIntroduction ? 'Analyzing our conversation...' : 'Generating your session highlights...'}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {isIntroduction
                  ? 'Assessing your level and creating your learning plan'
                  : "I'm reviewing what we covered together"}
              </p>
            </div>
            {!isIntroduction && (
              <div className="pt-2 space-y-3 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <Button
                  variant="outline"
                  onClick={() => navigate('/history')}
                  className="border-amber-200 hover:bg-amber-50"
                >
                  Skip → View Later in History
                </Button>
                <p className="text-xs text-gray-500">
                  You can check your session summary in session history anytime
                </p>
              </div>
            )}
          </div>
        </WarmCard>
      </div>
    )
  }

  const highlights = session.highlights as SessionHighlights | null
  const isCancelled = session.status === 'cancelled'

  // Cancelled session state
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-gradient-to-r from-gray-50/60 via-white to-gray-50/60 border-b border-gray-200/30 shadow-sm">
          <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              <h1 className="text-lg md:text-xl font-semibold text-gray-800">Session Summary</h1>
            </div>
            <Button size="sm" onClick={() => navigate('/practice')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25">
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">New Session</span>
            </Button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50/80 via-white to-gray-50/80 rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden animate-fade-in">
              <div className="h-1.5 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300" />
              <div className="p-8 text-center space-y-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-gray-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-700">Session Too Short</h2>
                  <p className="text-gray-500 mt-2">
                    This session was too brief for detailed analysis. Try practicing for at least 2 minutes with a few exchanges!
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/practice', {
                    state: {
                      lessonType: session?.lesson_type,
                      unitId: session?.unit_id,
                    }
                  })}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-r from-amber-50/60 via-white to-orange-50/60 border-b border-amber-200/30 shadow-sm">
        <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <h1 className="text-lg md:text-xl font-semibold text-gray-800">Session Summary</h1>
          </div>
          <Button size="sm" onClick={() => navigate('/practice')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25">
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">New Session</span>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {highlights && (
            <HighlightsRenderer highlights={highlights} />
          )}
        </div>
      </main>
    </div>
  )
}
